import React, { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon } from 'lucide-react';
import { CheckoutButton } from './CheckoutButton';
import { LIGHTBOX_EVENT } from './Lightbox/Lightbox';
import { PRICE } from '../data/checkout';
import { OFFER_ANCHOR } from '../data/woodworking';
import { primaryButtonClass } from '../utils/buttonStyles';
const HIDE_WHEN_VISIBLE = ['hero-cta', 'offer', 'offer-cta', 'final-cta', 'site-footer'];
type Mode = 'hidden' | 'teaser' | 'buy';
const inView = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
};

/** Mobile-only bar: teaser without price until the offer price was seen, then the buy bar. */
export function StickyBuyBar() {
  const [mode, setMode] = useState<Mode>('hidden');
  const seenOffer = useRef(false);
  const lightboxOpen = useRef(false);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!seenOffer.current && inView('offer-price')) seenOffer.current = true;
      const pastHero = window.scrollY > window.innerHeight * 0.6;
      if (lightboxOpen.current || !pastHero || HIDE_WHEN_VISIBLE.some(inView)) setMode('hidden');else setMode(seenOffer.current ? 'buy' : 'teaser');
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    const onLightbox = (event: Event) => {
      lightboxOpen.current = Boolean((event as CustomEvent<boolean>).detail);
      schedule();
    };
    update();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    window.addEventListener(LIGHTBOX_EVENT, onLightbox);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener(LIGHTBOX_EVENT, onLightbox);
    };
  }, []);
  const visible = mode !== 'hidden';
  return <div aria-hidden={!visible} className={`fixed inset-x-3 z-40 transition-[transform,opacity] duration-[250ms] md:hidden ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-[calc(100%+24px)] opacity-0'}`} style={{
    bottom: 'calc(12px + env(safe-area-inset-bottom))',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
  }} data-id="element-333" data-mp="2f250chm9lr47">
      <div className="flex items-center justify-between gap-3 rounded-[14px] bg-charcoal-raised py-2 pl-4 pr-2 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.7)] ring-1 ring-brass-light/25" data-id="element-334" data-mp="2xzfyfyw5uei2">
        {mode === 'buy' ? <>
            <p className="min-w-0 leading-tight" data-id="element-335" data-mp="37sn0pavltedx">
              <span className="block text-[0.66rem] font-semibold uppercase tracking-[0.12em] text-brass-light" data-id="element-336" data-mp="32r7e3eds2ztf">Complete collection</span>
              <span className="mt-1 block font-brand text-[1.2rem] font-bold text-ivory" data-id="element-337" data-mp="3gri7ao2k8qhr">{PRICE.label}</span>
            </p>
            <CheckoutButton label="GET IT NOW" size="sm" className="shrink-0" tabIndex={visible ? 0 : -1} data-id="element-338" data-mp="3bqazip0f3rb3" />
          </> : <>
            <p className="min-w-0 leading-tight" data-id="element-339" data-mp="46l7f9h4lnd3">
              <span className="block text-[0.66rem] font-semibold uppercase tracking-[0.12em] text-brass-light" data-id="element-340" data-mp="45a04zoh1xh8">Complete collection</span>
              <span className="mt-1 block text-[0.86rem] font-semibold text-ivory" data-id="element-341" data-mp="3qe2y3m25bd8h">30 projects + 5 bonuses</span>
            </p>
            <a href={OFFER_ANCHOR} tabIndex={visible ? 0 : -1} className={`${primaryButtonClass('sm')} shrink-0`} data-id="element-342" data-mp="3clv6s9tmseh6">
              <span data-id="element-343" data-mp="3hg92yeg4apmw">GET THE PLANS</span>
              <ArrowDownIcon className="h-[18px] w-[18px] shrink-0" strokeWidth={2.5} aria-hidden data-id="element-344" data-mp="32ps2axbb43r2" />
            </a>
          </>}
      </div>
    </div>;
}